import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren
} from '@angular/core';
import {PollsService} from '../../_services/polls.service';
import {Subscription, take} from 'rxjs';
import {NestedOption, Poll, PollOption, PollQuestion} from '../../_types/polls';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Community, UserAccessLevel} from "../../_types/communities";
import {ContentService} from "../../_services/content.service";
import {SnackbarService} from "../../_services/snackbar.service";
import {CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem} from "@angular/cdk/drag-drop";
import {Router} from "@angular/router";
import {User} from "../../_types/user";
import {AuthService} from "../../_services/auth.service";
import moment from "moment";
import {CommunitiesService} from "../../_services/communities.service";

@Component({
    selector: 'app-poll-view[pollId], app-poll-view[pollData]',
    templateUrl: './poll-view.component.html',
    styleUrl: './poll-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('barWidthAnimation', [
            state('*', style({width: '{{width}}'}), {params: {width: '0%'}}),
            transition('* => *', [
                animate('2000ms cubic-bezier(0.4, 0.0, 0.2, 1)')
            ])
        ])
    ],
    standalone: false
})
export class PollViewComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
    @Input() pollData!: Poll;
    @Input() userToken?: string;
    @Input() pollId?: number | string;
    @Input() small: boolean = false;
    @Input() preview: boolean = false;
    @Input() voteButtonText?: string | null;
    @Input() forceShowResults: boolean = false;
    @Output() updatePoll: EventEmitter<Poll> = new EventEmitter<Poll>();
    community: Community | null = null
    showResult: boolean = false;
    voteSending: boolean = false;
    isMobile: boolean = false;
    steps!: number;
    currentUser: User;

    @ViewChildren(CdkDropList) allDropLists!: QueryList<CdkDropList>;
    connectedDropLists: CdkDropList[] = [];
    private dropListChangesSubscription: Subscription | undefined;

    constructor(
        private readonly pollsService: PollsService,
        private readonly cdr: ChangeDetectorRef,
        private readonly contentService: ContentService,
        private readonly communitiesService: CommunitiesService,
        private readonly snack: SnackbarService,
        private router: Router,
        private authService: AuthService
    ) {
        this.communitiesService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
                this.cdr.markForCheck();
            });
        this.currentUser = this.authService.getUser();
    }

    get hasTimePassed(): boolean {
        if (!this.pollData?.end_at) return false
        const now = new Date();
        const targetDate = new Date(this.pollData?.end_at);
        return targetDate < now;
    }

    get voteButtonLabel(): string {
        const stored = this.pollData && 'vote_button_text' in this.pollData
            ? this.pollData.vote_button_text : this.community?.vote_button_text;
        return this.voteButtonText?.trim() || stored?.trim() || 'Vote';
    }

    get pollVoteValid(): boolean {
        return this.pollData.questions.every(question => this.questionPollVoteValid(question));
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any): void {
        this.isMobile = window.innerWidth <= 1000;
    }

    ngOnInit(): void {
        this.isMobile = window.innerWidth <= 1000;
    }

    ngOnDestroy(): void {
        if (this.dropListChangesSubscription) {
            this.dropListChangesSubscription.unsubscribe();
        }
    }

    ngAfterViewInit(): void {
        this.connectedDropLists = this.allDropLists.toArray();
        this.dropListChangesSubscription = this.allDropLists.changes.subscribe((): void => {
            this.connectedDropLists = this.allDropLists.toArray();
            this.cdr.detectChanges();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Load once all inputs are available, including newsletter capabilities.
        if ((changes['pollId'] || changes['userToken']) && this.pollId) {
            this.pollsService.getPollById(Number(this.pollId), this.userToken)
                .pipe(take(1)).subscribe({
                    next: poll => this.updateData(poll),
                    error: () => this.snack.openErrorSnackBar('This poll link is invalid or expired. Please log in or request a new link.')
                });
        }
        if (changes['pollData']?.currentValue) {
            this.updateData(changes['pollData']?.currentValue);
        }
    }

    /** Whether this view is being used by a poll participant. */
    get isParticipant(): boolean {
        return !!this.userToken || this.hasCommunityRole(['member']);
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.communitiesService.userHasCommunityRole(roles);
    }

    vote(): void {
        this.voteSending = true;
        let options: any[] = []
        for (let question of this.pollData.questions) {
            if (question.results?.length && question.results?.length > 0) {
                options = options.concat(question.results)
            }
            for (let option of question.options) {
                if (option.nestedResults?.length && option.nestedResults?.length > 0) {
                    options = options.concat(option.nestedResults)
                }
            }
        }

        let options_answers: {
            option: number,
            value?: number,
            text?: string,
            order?: number,
            selected?: boolean,
            click_amount?: number
        }[] = [];
        for (let question of this.pollData.questions) {
            if (question.type == 'slider') {
                for (let option of question.options) {
                    options.push(option.id)
                    options_answers.push({
                        option: option.id,
                        value: option.value
                    })
                }
            } else if (question.type == 'order') {
                for (let i: number = 0; i < question.options?.length; i++) {
                    options.push(question.options[i].id)
                    options_answers.push({
                        option: question.options[i].id,
                        order: i
                    })
                }
            } else if (question.type == 'select') {
                for (let option of question.options) {
                    if (options.includes(option.id)) {
                        if (option.is_other_option) {
                            options_answers.push({
                                option: option.id,
                                text: option.freeText,
                                selected: true
                            })
                        } else {
                            options_answers.push({
                                option: option.id,
                                selected: true
                            })
                        }
                    }
                }
            } else if (question.type == 'click_game') {
                for (let option of question.options) {
                    options_answers.push({
                        option: option.id,
                        selected: options.includes(option.id),
                        click_amount: option.clickedCount || 0
                    })
                }
            } else if (question.type == 'lists') {

                for (let option of question.options) {
                    options.push(option.id)
                    let answer: { option: number, list_options: number[] } = {
                        option: option.id,
                        list_options: []
                    }
                    for (let nestedOption of option.nested_options) {
                        answer.list_options.push(nestedOption.id)
                    }
                    options_answers.push(answer)
                }
            } else if (question.type == 'text') {
                for (let option of question.options) {
                    options.push(option.id)
                    options_answers.push({
                        option: option.id,
                        text: option.freeText
                    })
                }
            }
        }

        this.pollsService.votePoll(this.pollData.id, options, options_answers, this.userToken)
            .pipe(
                take(1)
            )
            .subscribe({
                next: (res): void => {
                    this.voteSending = false;
                    this.pollData = res.data;
                    this.showResult = true;
                    this.updatePoll.emit(res.data);
                    this.cdr.detectChanges();
                    if (!this.userToken && this.pollData.ai_comment_builder && res.data.post_id) {
                        this.generateAiSummary(res.data.id, res.data.post_id)
                    }
                }, error: (e): void => {
                    this.voteSending = false;
                    this.cdr.detectChanges();
                    this.snack.openErrorSnackBar(e)
                }
            })
    }

    generateAiSummary(pollId: number, postId: number): void {
        this.contentService.generateAIPollSummaryAndListenForResponse(this.currentUser.id, pollId)
            .subscribe({
                next: (AiResponse): void => {
                    const text = AiResponse?.data?.draft ?? '';

                    if (text && postId) {
                        //     Once the response is finished, then direct the user to the job id
                        this.router.navigate(['/feed'], {
                            queryParams: {
                                AICommentBuilderJob: AiResponse.data.job_id,
                                post: postId
                            }
                        });
                    } else {
                        this.snack.openErrorSnackBar(AiResponse?.data?.error ? 'Error: ' + AiResponse?.data?.error : 'API turned and invalid response please try again.');
                    }
                },
                error: (e): void => {
                    console.error(e)
                    this.snack.openErrorSnackBar(e)
                }
            });
    }

    public questionPollVoteValid(question: PollQuestion): boolean {

        if (question.type == 'slider' || question.type == 'order') {
            // the default values are valid
            return true;
        }
        if (question.type == 'select') {
            // If other option is selected then they must answer it
            let valid: boolean = true;
            for (let option of question.options) {
                if (option.is_other_option && question.results?.includes(option.id) && !option.freeText) {
                    valid = false;
                }
            }
            if (!valid) {
                return false;
            }
        }

        if (question.type == 'text') {
            let valid: boolean = true;
            for (let option of question.options) {
                if (option.freeText == null || option.freeText == '' || option.freeText.length < 0 || option.freeText.length > 255) {
                    valid = false;
                }
            }
            return valid;
        }

        if (question.type == 'lists') {
            let valid: boolean = true;
            for (let option of question.options) {
                if ((option.slider_max != null && option.nested_options.length > option.slider_max) || (option.slider_min != null && option.nested_options.length < option.slider_min)) {
                    valid = false;
                }
            }
            return valid;
        }

        const hasMatchingId = (options: PollOption[] | NestedOption[], results: number[]): boolean => {
            return options.some(option => results?.length ? results.includes(option.id) : false);
        };

        const checkNestedOptions = (option: PollOption): boolean => {
            if (option.nested_options?.length && question.results?.includes(option.id)) {
                if (!option.nestedResults) return false;
                if (!hasMatchingId(option.nested_options, option.nestedResults)) {
                    return false;
                }
                for (const nestedOption of option.nested_options) {
                    if (!checkNestedOptions(nestedOption as any)) {
                        return false;
                    }
                }
            }
            return true;
        };

        // If the question is displayed conditionally then it is valid if the conditions aren't met.
        if (question.display_condition_options.length > 0 && !this.displayConditionsMet(question)) {
            return true;
        }

        if (!hasMatchingId(question.options, question?.results as any)) {
            return false;
        }
        for (const option of question.options) {
            if (!checkNestedOptions(option)) {
                return false;
            }
        }
        return true;
    }

    getPercentage(number: number, totalNumber: number): string {
        return ((number / (totalNumber || 1)) * 100).toFixed(1) + '%';
    }


    setResult(item: any, array: string, option: PollOption | NestedOption, isMultiple: boolean): void {
        let updatedValue: number[] = [];
        // if current option is not 'N/a' then loop through question and remove values for 'na'
        if (isMultiple) {
            if (option.not_applicable) {
                item[array] = [option.id];
            } else {
                if (item[array]?.includes(option.id)) {
                    updatedValue = item[array]?.filter((val: number) => val !== option.id) ?? [];
                    item[array] = updatedValue;
                } else {
                    updatedValue = [...(item[array] ?? []), option.id];
                    item[array] = updatedValue;
                }
                // if current option is not 'N/a' then loop through question and remove values for 'na'
                for (let question of this.pollData.questions) {
                    for (let option of question.options) {
                        if (option.not_applicable && item[array].includes(option.id)) {
                            updatedValue = item[array]?.filter((val: number) => val !== option.id) ?? [];
                            item[array] = updatedValue;
                        }
                        for (let nestedOption of option.nested_options) {
                            if (nestedOption.not_applicable && item[array].includes(nestedOption.id)) {
                                updatedValue = item[array]?.filter((val: number) => val !== option.id) ?? [];
                                item[array] = updatedValue;
                            }
                        }
                    }
                }
            }
        } else {
            if (this.pollData.matrix_exclusive) {
                //     If matrix exclusive. then get index of option and loop through all questions and unselect any other question option with the same index
                const selectedIndex: number = item.options.findIndex((x: any): boolean => x.id === option.id);

                for (let question of this.pollData.questions) {
                    // as matrix_exclusive and !isMultiple then there will only ever be one result in question.results
                    if (question.results && Array.isArray(question.results) && question.results[0]) {
                        // @ts-ignore
                        let questionSelectionIndex: number = question.options.findIndex((x: any): boolean => x.id === question.results[0]);
                        if (questionSelectionIndex == selectedIndex) {
                            // this option has been selected elsewhere so remove.
                            question.results = [];
                        }
                    }
                }
            }
            item[array] = [option.id];
        }

        this.pollData.questions.forEach(question => {
            const questionOptions = question.options.map(opt => opt.id);
            if (!this.displayConditionsMet(question)) {
                question['results'] = (question['results'] || []).filter(res => !questionOptions.includes(res));
            }
        });

        // Uncheck/change parent will clear checked nested options
        if (array === 'results') {
            item.options.filter((opt: PollOption) => !item[array]?.includes(opt.id) && opt.nested_options)
                .forEach((opt: PollOption) => {
                    opt['nestedResults'] = [];
                })
        }
    }

    updateData(poll: Poll): void {
        this.showResult = this.hasTimePassed || this.forceShowResults || !!poll?.user_vote?.length || (!this.isParticipant && !this.hasCommunityRole(['full_admin']));
        // this.showResult = false; // TEST line ensure is commented
        if (!this.showResult) {
            this.pollData = this.orderPoll(poll);
            this.pollData = this.setupPollTypes(poll)
        } else {
            this.pollData = poll;
        }

        if (!this.showResult) {
            this.setRandomValuesForClickGame()
        }
        this.cdr.detectChanges();
    }

    setRandomValuesForClickGame(): void {
        for (let question of this.pollData.questions) {
            if (question.type == 'click_game' && !question.disable_animation) {
                let array = []
                for (let i = 1; i <= question.options?.length; i++) {
                    array.push(i);
                }
                let randomOrderArray = this.randomize(array)
                for (let i: number = 0; i < question.options?.length; i++) {
                    question.options[i].animated = false
                    question.options[i].size = this.getRandomNumber(0.6, 1);
                    question.options[i].animated = true;
                    if (question.randomize_options) {
                        question.options[i].order = randomOrderArray[i]
                    }
                }
            }
        }
        this.cdr.detectChanges();
    }

    setupPollTypes(poll: Poll): Poll {
        for (let question of poll.questions) {
            if (question.type == 'order') {
                this.setOrderResultsArray(question)
            }
        }
        return poll
    }

    displayConditionsMet(question: PollQuestion): boolean {
        let numberOfConditions: number = question.display_condition_options.length
        let metConditionsNumber: number = 0;
        let conditionsMet: boolean = true;
        if (question.display_condition_options.length > 0 && !this.showResult) {
            for (let condition of question.display_condition_options) {
                for (let question of this.pollData.questions) {
                    if (question.results?.includes(condition)) {
                        metConditionsNumber++
                    }
                    for (let option of question.options) {
                        if (option.nestedResults?.includes(condition)) {
                            metConditionsNumber++
                        }
                    }
                }
            }
            // tally for each time a condition is met. once the same amount fo conditions have been met that exist then mark as met
            conditionsMet = metConditionsNumber === numberOfConditions;
        } else {
            conditionsMet = true;
        }
        return conditionsMet
    }

    changeVote(): void {
        this.orderPoll(this.pollData);
        this.showResult = false
    }

    orderPoll(poll: Poll): Poll {
        if (poll.shuffle_questions) {
            this.randomize(poll.questions);
        } else if (poll.questions) {
            poll?.questions?.sort((a, b) => a.position - b.position);
        }

        // Randomize options if boolean is set
        for (let question of poll.questions) {
            if (question.randomize_options) {
                // Only randomize options that aren't fixed
                question.options = this.randomize(question.options);
            }
            for (let option of question.options) {
                if (option.nested_options && option.nested_options.length > 0 && option.randomize_nested_options) {
                    // Only randomize options that aren't fixed
                    option.nested_options = this.randomize(option.nested_options);
                }
            }
        }
        return poll
    }

    shuffleArray(array: any) {
        for (let i: number = array.length - 1; i > 0; i--) {
            const j: number = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    randomize(arr: any) {
        // Step 1: Find the indices of objects that have fixed false
        let matchingIndices = arr.reduce((acc: any, obj: any, index: number) => {
            if (!obj['fixed']) {
                acc.push(index);
            }
            return acc;
        }, []);

        // Step 2: Extract matching objects based on their indices
        let matchingObjects = matchingIndices.map((index: number) => arr[index]);

        // Step 3: Shuffle the extracted objects
        matchingObjects = this.shuffleArray(matchingObjects);

        // Step 4: Put the shuffled objects back in their original positions
        matchingIndices.forEach((index: number, i: number): void => {
            arr[index] = matchingObjects[i];
        });

        return arr;
    }


    getQuestion(selectedIndex: number): PollQuestion {
        return this.pollData.questions[selectedIndex];
    }

    updateSteps(steps: number): void {
        this.steps = steps;
        this.cdr.detectChanges();
    }

    drop(event: CdkDragDrop<string[]>, question: PollQuestion): void {

        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex,
            );
        }
        // update the question.results array to reflect new order
        this.setOrderResultsArray(question);
    }

    dropList(event: CdkDragDrop<string[]>): void {

        if (event.previousContainer === event.container) {
            // Moving within the same nested list
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            // Moving from one nested list to another
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex,
            );
        }

        this.cdr.detectChanges();
    }

    setOrderResultsArray(question: PollQuestion): void {
        let optionIds: number[] = []
        for (let option of question.options) {
            optionIds.push(option.id);
        }
        question.results = optionIds
    }

    getRandomNumber(min: number, max: number): number {
        if (min > max) {
            [min, max] = [max, min];
        }
        return Math.random() * (max - min) + min;
    }

    calculateSVGWidth(optionsLength: number, clickContainer: HTMLElement): number {
        const rect: DOMRect = clickContainer.getBoundingClientRect();
        const width: number = rect.width;
        return (width / optionsLength - 10)
    }

    setClickResult(question: any, array: string, option: PollOption | NestedOption, isMultiple: boolean): void {
        this.setResult(question, array, option, isMultiple);

        if (question.max_responses && question?.results?.length >= question.max_responses) {
            question.end = "<p>You have selected the maximum number of responses</p>"
        } else if (!question.max_responses) {
            question.end = `<p>${option.text}</p><br><p>${option.description}</p>`;
        }
    }

    clickOption(option: PollOption): void {
        if (option.clickedCount) {
            option.clickedCount++
        } else {
            option.clickedCount = 1;
        }
    }

    restartGame(question: any): void {
        question['results'] = [];
        question.end = null;
        question.start = true;
        question.paused = false;
        for (let option of question.options) {
            option.clickedCount = 0;
        }
    }

    pauseAnimation(question: any): void {
        if (!question.multiple_choices || (question.max_responses && question?.results?.length >= question.max_responses)) {
            question.paused = true;
        }
    }

    setSliderValue(option: PollOption, event: number): void {
        option.value = event
    }

    orderOptions(options: PollOption[], orderKey: 'user_order' | 'average_order'): PollOption[] {
        // @ts-ignore
        return options.sort((a, b): number => a[orderKey] - b[orderKey]);
    }

    currencySliderFormat(value: number): string {
        if (value === null) {
            return '0.00';
        } else {
            return value.toFixed(2);
        }
    }

    defaultSliderFormat(value: number): string {
        if (value === null) {
            return '0';
        } else {
            return String(value);
        }
    }

    isPast(value: string | number | Date,): boolean {
        return moment(value).isBefore(moment());
    }
}
